/**
 * Install Manager Module
 * Handles installation wizard flow and persistence
 */

// Prevent redeclaration if script is loaded multiple times
if (typeof InstallManager === 'undefined') {
    class InstallManager {
        constructor() {
            this.currentStep = 1;
            this.totalSteps = 4;
            this.selectedTheme = 'light'; // 'light' or 'dark'
            this.email = '';
            this.discountCode = 'ACE-20';
            this.windowId = 'install-wizard';
        }

        /**
         * Check if install wizard should be shown
         */
        shouldShowInstall() {
            return !localStorage.getItem('smash_install_completed');
        }

        /**
         * Show installation wizard
         */
        async showInstallWizard() {
            // Get discount code from theme settings
            if (typeof theme !== 'undefined' && theme.settings && theme.settings.discount_code) {
                this.discountCode = theme.settings.discount_code;
            }

            // Close all existing windows before starting install wizard
            this.closeAllExistingWindows();

            // Preload wallpaper images for instant switching
            this.preloadWallpapers();

            // Hide desktop icons and taskbar immediately
            this.hideDesktopUI();

            // Also hide after a short delay to ensure DOM is ready
            setTimeout(() => {
                this.hideDesktopUI();
            }, 100);

                // Open wizard window
                if (window.smashOS && window.smashOS.windowManager) {
                await window.smashOS.windowManager.open(this.windowId, {
                    title: 'Welcome to Smash Tennis',
                    icon: null,
                    section: 'window-install-wizard',
                    size: { width: '700px', height: 'auto' },
                    position: { x: '50%', y: '50%' }, // Center on screen
                    isModal: true,
                    canResize: false,
                    canMaximize: false,
                    showStatusBar: false,
                    showMenuBar: false
                });

                // Ensure window is centered after content loads
                const windowElement = document.getElementById(`window-${this.windowId}`);
                if (windowElement) {
                    this.ensureCentered(windowElement);

                    // Listen for window close event to restore desktop UI
                    const handleWindowClose = (e) => {
                        if (e.detail && e.detail.windowId === this.windowId) {
                            console.log('InstallManager: Install wizard closed, saving preferences and restoring desktop UI');
                            
                            // Save localStorage values (same as cancel button)
                            localStorage.setItem('smash_install_completed', 'true');
                            localStorage.setItem('smash_last_boot_timestamp', Date.now().toString());
                            
                            // Save theme preference if one was selected
                            if (this.selectedTheme) {
                                localStorage.setItem('smash_install_theme', this.selectedTheme);
                                localStorage.setItem('smash_theme_preference', this.selectedTheme);
                            }
                            
                            this.showDesktopUI();
                            // Remove listener after use
                            windowElement.removeEventListener('window:close', handleWindowClose);
                        }
                    };
                    windowElement.addEventListener('window:close', handleWindowClose);
                }

                // Initialize wizard after window is created
                setTimeout(() => {
                    this.initWizard();
                }, 500);
            }
        }

        /**
         * Close all existing windows on screen (before starting install wizard)
         */
        closeAllExistingWindows() {
            if (!window.smashOS || !window.smashOS.windowManager) {
                return;
            }

            const windowManager = window.smashOS.windowManager;

            // Get all window IDs from the windows Map
            const allWindowIds = Array.from(windowManager.windows.keys());

            // Close all windows immediately
            allWindowIds.forEach(windowId => {
                const windowElement = document.getElementById(`window-${windowId}`);
                if (windowElement) {
                    // Remove from DOM immediately
                    windowElement.remove();
                }

                // Clean up window manager registry
                if (windowManager.windows) {
                    windowManager.windows.delete(windowId);
                }

                // Remove from taskbar
                if (window.smashOS && window.smashOS.desktopManager) {
                    window.smashOS.desktopManager.removeWindowFromTaskbar(windowId);
                }
            });

            // Clear active window
            if (windowManager.activeWindow) {
                windowManager.activeWindow = null;
            }
        }

        /**
         * Preload wallpaper images for instant theme switching
         */
        preloadWallpapers() {
            if (!window.smashOSAssets) {
                console.warn('InstallManager: Asset URLs not available for preloading');
                return;
            }

            const lightUrl = window.smashOSAssets.wallpaperLight;
            const darkUrl = window.smashOSAssets.wallpaperDark;

            // Preload light wallpaper
            if (lightUrl) {
                const lightImg = new Image();
                lightImg.src = lightUrl;
                console.log('InstallManager: Preloading light wallpaper:', lightUrl);
            }

            // Preload dark wallpaper
            if (darkUrl) {
                const darkImg = new Image();
                darkImg.src = darkUrl;
                console.log('InstallManager: Preloading dark wallpaper:', darkUrl);
            }
        }

        /**
         * Hide desktop icons and taskbar (logo stays visible)
         */
        hideDesktopUI() {
            const desktopIcons = document.getElementById('desktop-icons');
            const taskbar = document.getElementById('taskbar');

            console.log('InstallManager: Hiding desktop UI', { desktopIcons, taskbar });

            if (desktopIcons) {
                desktopIcons.classList.remove('visible');
            }
            if (taskbar) {
                taskbar.classList.remove('visible');
            }
            // Logo stays visible (handled by CSS)
        }

        /**
         * Show desktop icons and taskbar (logo stays visible)
         * Now uses the main showDesktopUI() from theme.js which waits for wallpaper
         */
        showDesktopUI() {
            // Use the main showDesktopUI() function which properly waits for wallpaper
            if (window.smashOS && window.smashOS.showDesktopUI) {
                window.smashOS.showDesktopUI();
            } else {
                // Fallback if main function not available
                const desktopIcons = document.getElementById('desktop-icons');
                const taskbar = document.getElementById('taskbar');

                console.log('InstallManager: Showing desktop UI (fallback)', { desktopIcons, taskbar });

                if (desktopIcons) {
                    desktopIcons.classList.add('visible');
                }
                if (taskbar) {
                    taskbar.classList.add('visible');
                }
            }
        }

        /**
         * Ensure window is centered on screen
         */
        ensureCentered(windowElement) {
            if (!windowElement) return;

            // Use transform-based centering for better results
            windowElement.style.position = 'fixed';
            windowElement.style.left = '50%';
            windowElement.style.top = '50%';
            windowElement.style.transform = 'translate(-50%, -50%)';
            windowElement.style.margin = '0';
            windowElement.style.right = 'auto';
            windowElement.style.bottom = 'auto';

            // Store that this window is centered
            windowElement.dataset.centered = 'true';

            // Also store in window data
            if (window.smashOS && window.smashOS.windowManager) {
                const windowData = window.smashOS.windowManager.windows.get(this.windowId);
                if (windowData) {
                    windowData.position = { x: '50%', y: '50%', centered: true };
                }
            }

            if (windowElement._resizeListener) {
                window.removeEventListener('resize', windowElement._resizeListener);
                windowElement._resizeListener = null;
            }

            // Add resize listener to re-center on viewport changes
            const reCenter = () => {
                if (windowElement && windowElement.dataset.centered === 'true') {
                    windowElement.style.left = '50%';
                    windowElement.style.top = '50%';
                    windowElement.style.transform = 'translate(-50%, -50%)';
                }
            };

            windowElement._resizeListener = reCenter;
            window.addEventListener('resize', reCenter);
        }

        /**
         * Initialize wizard UI and event handlers
         */
        initWizard() {
            const wizardContainer = document.getElementById('install-wizard');
            if (!wizardContainer) {
                console.warn('InstallManager: Wizard container not found');
                return;
            }

            // Show first step
            this.showStep(1);

            // Setup event handlers
            this.setupEventHandlers();
        }

        /**
         * Setup event handlers for wizard buttons
         */
        setupEventHandlers() {
            const wizardContainer = document.getElementById('install-wizard');
            if (!wizardContainer) {
                console.warn('InstallManager: Wizard container not found for event handlers');
                return;
            }

            // Use single event listener with delegation
            const handleClick = (e) => {
                const target = e.target;
                const button = target.closest('button');

                if (!button) return;

                // Check both class names and data-action attributes
                const hasClass = (cls) => button.classList.contains(cls);
                const hasAction = (action) => button.dataset.action === action;

                // Next button
                if (hasClass('st-install-wizard__btn-next') || hasAction('next')) {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('InstallManager: Next button clicked', button);
                    this.handleNext();
                    return;
                }

                // Back button
                if (hasClass('st-install-wizard__btn-back') || hasAction('back')) {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('InstallManager: Back button clicked', button);
                    this.handleBack();
                    return;
                }

                // Cancel button
                if (hasClass('st-install-wizard__btn-cancel') || hasAction('cancel')) {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('InstallManager: Cancel button clicked', button);
                    this.handleCancel();
                    return;
                }

                // Shop Now button
                if (hasClass('st-install-wizard__btn-shop') || hasAction('shop')) {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('InstallManager: Shop Now button clicked', button);
                    this.handleShopNow();
                    return;
                }
            };

            // Add click listener to wizard container with capture phase
            wizardContainer.addEventListener('click', handleClick, true);

            // Also add to document as fallback with capture phase
            const documentHandler = (e) => {
                if (e.target.closest('#install-wizard')) {
                    handleClick(e);
                }
            };
            document.addEventListener('click', documentHandler, true);

            // Store handler for cleanup
            wizardContainer._clickHandler = handleClick;
            wizardContainer._documentHandler = documentHandler;

            // Email form submission - handle via AJAX
            const emailForm = document.getElementById('install-email-form');
            if (emailForm) {
                emailForm.addEventListener('submit', (e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    try {
                        const formData = new FormData(emailForm);
                        const submitButton = emailForm.querySelector('button[type="submit"]');
                        const errorDiv = document.getElementById('install-email-error');

                        // Remove previous errors
                        if (errorDiv) {
                            errorDiv.style.display = 'none';
                            errorDiv.textContent = '';
                        }

                        // Disable submit button if exists
                        if (submitButton) {
                            submitButton.disabled = true;
                            const originalText = submitButton.textContent;
                            submitButton.textContent = 'SUBMITTING...';
                        }

                        // Get the form action URL (Shopify sets this automatically)
                        const formAction = emailForm.action || '/contact';

                        // Submit to Shopify's customer endpoint
                        fetch(formAction, {
                            method: 'POST',
                            body: formData,
                            headers: {
                                'X-Requested-With': 'XMLHttpRequest',
                            },
                        })
                            .then((response) => {
                                // Simple success check - if status is 200, 302, or 0 (CORS), show success
                                if (response.status === 0 || response.status === 200 || response.status === 302) {
                                    // Success - proceed to next step
                                    this.email = formData.get('contact[email]');
                                    // Update discount code display
                                    const codeDisplay = document.getElementById('install-discount-code');
                                    if (codeDisplay) {
                                        codeDisplay.textContent = this.discountCode;
                                    }
                                    this.showStep(4);
                                    if (submitButton) {
                                        submitButton.disabled = false;
                                        submitButton.textContent = submitButton.textContent.replace('SUBMITTING...', 'Next >');
                                    }
                                    return;
                                }

                                // Try to read response for error details
                                return response
                                    .text()
                                    .then((html) => {
                                        if (!html || html.trim().length === 0) {
                                            // Empty response - assume success
                                            this.email = formData.get('contact[email]');
                                            // Update discount code display
                                            const codeDisplay = document.getElementById('install-discount-code');
                                            if (codeDisplay) {
                                                codeDisplay.textContent = this.discountCode;
                                            }
                                            this.showStep(4);
                                            if (submitButton) {
                                                submitButton.disabled = false;
                                                submitButton.textContent = submitButton.textContent.replace('SUBMITTING...', 'Next >');
                                            }
                                            return;
                                        }

                                        // Check for obvious error patterns
                                        const htmlLower = html.toLowerCase();
                                        const hasError =
                                            htmlLower.includes('invalid') ||
                                            htmlLower.includes('error') ||
                                            htmlLower.includes('required') ||
                                            htmlLower.includes('already exists');

                                        if (hasError && response.status >= 400) {
                                            // Show error
                                            if (errorDiv) {
                                                errorDiv.textContent = 'Please enter a valid email address.';
                                                errorDiv.style.display = 'block';
                                            }

                                            if (submitButton) {
                                                submitButton.disabled = false;
                                                submitButton.textContent = submitButton.textContent.replace('SUBMITTING...', 'Next >');
                                            }
                                        } else {
                                            // Assume success if no clear errors
                                            this.email = formData.get('contact[email]');
                                            // Update discount code display
                                            const codeDisplay = document.getElementById('install-discount-code');
                                            if (codeDisplay) {
                                                codeDisplay.textContent = this.discountCode;
                                            }
                                            this.showStep(4);
                                            if (submitButton) {
                                                submitButton.disabled = false;
                                                submitButton.textContent = submitButton.textContent.replace('SUBMITTING...', 'Next >');
                                            }
                                        }
                                    })
                                    .catch(() => {
                                        // If we can't parse response, assume success (form likely submitted)
                                        this.email = formData.get('contact[email]');
                                        // Update discount code display
                                        const codeDisplay = document.getElementById('install-discount-code');
                                        if (codeDisplay) {
                                            codeDisplay.textContent = this.discountCode;
                                        }
                                        this.showStep(4);
                                        if (submitButton) {
                                            submitButton.disabled = false;
                                            submitButton.textContent = submitButton.textContent.replace('SUBMITTING...', 'Next >');
                                        }
                                    });
                            })
                            .catch((error) => {
                                console.error('InstallManager: Form submission error:', error);
                                // On network error, still assume success (form may have submitted)
                                this.email = formData.get('contact[email]');
                                // Update discount code display
                                const codeDisplay = document.getElementById('install-discount-code');
                                if (codeDisplay) {
                                    codeDisplay.textContent = this.discountCode;
                                }
                                this.showStep(4);
                                if (submitButton) {
                                    submitButton.disabled = false;
                                    submitButton.textContent = submitButton.textContent.replace('SUBMITTING...', 'Next >');
                                }
                            });
                    } catch (error) {
                        console.error('InstallManager: Unexpected error:', error);
                        // On any error, assume success
                        const formData = new FormData(emailForm);
                        this.email = formData.get('contact[email]');
                        // Update discount code display
                        const codeDisplay = document.getElementById('install-discount-code');
                        if (codeDisplay) {
                            codeDisplay.textContent = this.discountCode;
                        }
                        this.showStep(4);
                    }
                });
            }

            // Court selection radio buttons - apply theme immediately on change
            const courtRadios = wizardContainer.querySelectorAll('input[name="court-selection"]');
            courtRadios.forEach(radio => {
                radio.addEventListener('change', (e) => {
                    if (e.target.checked) {
                        const selectedTheme = e.target.value;
                        console.log('InstallManager: Theme selected:', selectedTheme);
                        this.selectedTheme = selectedTheme;
                        this.applyTheme(selectedTheme);
                    }
                });
            });
        }

        /**
         * Show specific step
         */
        showStep(step) {
            const wizardContainer = document.getElementById('install-wizard');
            if (!wizardContainer) return;

            // Hide all steps
            const allSteps = wizardContainer.querySelectorAll('.st-install-wizard__step');
            allSteps.forEach(stepEl => {
                stepEl.style.display = 'none';
            });

            // Show current step
            const currentStepEl = wizardContainer.querySelector(`[data-step="${step}"]`);
            if (currentStepEl) {
                currentStepEl.style.display = 'flex';
                this.currentStep = step;
            }

            // Apply theme immediately when step 2 is shown (for default selection)
            if (step === 2) {
                const selectedRadio = document.querySelector('input[name="court-selection"]:checked');
                if (selectedRadio) {
                    const selectedTheme = selectedRadio.value;
                    this.selectedTheme = selectedTheme;
                    this.applyTheme(selectedTheme);
                }
            }
        }

        /**
         * Handle Next button
         */
        async handleNext() {
            if (this.currentStep === 1) {
                // Step 1 -> Step 2: Save theme selection (default to light)
                this.showStep(2);
            } else if (this.currentStep === 2) {
                // Step 2 -> Step 3: Save theme selection
                const selectedRadio = document.querySelector('input[name="court-selection"]:checked');
                if (selectedRadio) {
                    this.selectedTheme = selectedRadio.value;
                    this.applyTheme(this.selectedTheme);
                }
                this.showStep(3);
            } else if (this.currentStep === 3) {
                // Step 3 -> Step 4: Validate and trigger form submission
                const emailInput = document.getElementById('install-email');
                const errorDiv = document.getElementById('install-email-error');
                const emailForm = document.getElementById('install-email-form');

                if (!emailInput || !emailInput.value) {
                    if (errorDiv) {
                        errorDiv.textContent = 'Please enter your email address';
                        errorDiv.style.display = 'block';
                    }
                    return;
                }

                // Validate email format
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(emailInput.value)) {
                    if (errorDiv) {
                        errorDiv.textContent = 'Please enter a valid email address';
                        errorDiv.style.display = 'block';
                    }
                    return;
                }

                // Hide error
                if (errorDiv) {
                    errorDiv.style.display = 'none';
                }

                // Trigger form submission (form handler will proceed to step 4 on success)
                if (emailForm) {
                    emailForm.requestSubmit();
                } else {
                    // Fallback: if form not found, just proceed
                    this.email = emailInput.value;
                    this.showStep(4);
                }
            }
        }

        /**
         * Handle Back button
         */
        handleBack() {
            if (this.currentStep > 1) {
                this.showStep(this.currentStep - 1);
            }
        }

        /**
         * Handle Cancel button
         */
        handleCancel() {
            // Set localStorage to prevent installer from showing again on refresh
            localStorage.setItem('smash_install_completed', 'true');
            localStorage.setItem('smash_last_boot_timestamp', Date.now().toString());

            // Save theme preference if one was selected
            if (this.selectedTheme) {
                localStorage.setItem('smash_install_theme', this.selectedTheme);
                localStorage.setItem('smash_theme_preference', this.selectedTheme);
            }

            // Show desktop UI again
            this.showDesktopUI();

            // Close wizard window
            if (window.smashOS && window.smashOS.windowManager) {
                window.smashOS.windowManager.close(this.windowId);
            }
        }

        /**
         * Handle Shop Now button
         */
        handleShopNow() {
            // Apply discount code to cart
            this.applyDiscountCode();

            // Set localStorage (but don't show desktop UI yet - will show after navigation)
            localStorage.setItem('smash_install_completed', 'true');
            localStorage.setItem('smash_last_boot_timestamp', Date.now().toString());
            localStorage.setItem('smash_install_theme', this.selectedTheme);
            if (this.email) {
                localStorage.setItem('smash_install_email', this.email);
            }

            // Remove window from DOM immediately to prevent flickering
            const windowElement = document.getElementById(`window-${this.windowId}`);
            if (windowElement) {
                // Remove from window manager registry first
                if (window.smashOS && window.smashOS.windowManager) {
                    window.smashOS.windowManager.windows.delete(this.windowId);
                    // Remove from taskbar
                    if (window.smashOS.desktopManager) {
                        window.smashOS.desktopManager.removeWindowFromTaskbar(this.windowId);
                    }
                }
                // Remove from DOM immediately
                windowElement.remove();
            }

            // Navigate immediately (no animation delay)
            window.location.href = '/';
        }

        /**
         * Submit email to Shopify newsletter
         * TODO: Implement actual email submission
         */
        async submitEmailToShopify(email) {
            // Store email in localStorage for now (will implement actual submission later)
            localStorage.setItem('smash_pending_email', email);
            console.log('InstallManager: Email submission faked (stored in localStorage):', email);

            // Return immediately as resolved promise
            return Promise.resolve(true);
        }

        /**
         * Apply theme preference (light/dark)
         */
        applyTheme(theme) {
            const desktop = document.getElementById('desktop');
            if (!desktop) {
                console.warn('InstallManager: Desktop element not found');
                return;
            }

            console.log('InstallManager: Applying theme:', theme);

            // Get asset URLs from window.smashOSAssets (set in theme.liquid)
            let darkBgUrl, lightBgUrl;

            if (window.smashOSAssets) {
                darkBgUrl = window.smashOSAssets.wallpaperDark;
                lightBgUrl = window.smashOSAssets.wallpaperLight;
            } else {
                // Fallback: construct URL (Shopify assets are at /assets/filename)
                const baseUrl = window.location.origin;
                darkBgUrl = baseUrl + '/assets/wallpaper_dark.jpg';
                lightBgUrl = baseUrl + '/assets/wallpaper_light.jpg';
            }

            if (theme === 'dark') {
                desktop.setAttribute('data-theme', 'dark');
                // Update background image to dark version
                console.log('InstallManager: Setting dark wallpaper:', darkBgUrl);
                desktop.style.setProperty('background-image', `url('${darkBgUrl}')`, 'important');
                desktop.style.setProperty('background-size', 'cover', 'important');
                desktop.style.setProperty('background-position', 'center', 'important');
                desktop.style.setProperty('background-repeat', 'no-repeat', 'important');
            } else {
                desktop.removeAttribute('data-theme');
                // Update background image to light version
                console.log('InstallManager: Setting light wallpaper:', lightBgUrl);
                desktop.style.setProperty('background-image', `url('${lightBgUrl}')`, 'important');
                desktop.style.setProperty('background-size', 'cover', 'important');
                desktop.style.setProperty('background-position', 'center', 'important');
                desktop.style.setProperty('background-repeat', 'no-repeat', 'important');
            }

            // Store preference
            localStorage.setItem('smash_theme_preference', theme);

            // Update document theme attribute for CSS variables
            document.documentElement.setAttribute('data-theme', theme);
        }

        /**
         * Apply discount code to cart
         */
        applyDiscountCode() {
            // Store discount code in cart attributes
            if (typeof Shopify !== 'undefined' && Shopify.theme && Shopify.theme.cart) {
                // Add discount code to cart note or attributes
                fetch('/cart/update.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        attributes: {
                            'discount_code': this.discountCode
                        }
                    })
                }).catch(error => {
                    console.warn('InstallManager: Could not update cart attributes:', error);
                });
            }

            // Store in localStorage as fallback
            localStorage.setItem('smash_discount_code', this.discountCode);
            localStorage.setItem('smash_discount_applied_date', Date.now().toString());
        }

        /**
         * Complete installation
         */
        completeInstall() {
            // Set localStorage values
            localStorage.setItem('smash_install_completed', 'true');
            localStorage.setItem('smash_last_boot_timestamp', Date.now().toString());

            // Store installation data
            localStorage.setItem('smash_install_theme', this.selectedTheme);
            if (this.email) {
                localStorage.setItem('smash_install_email', this.email);
            }

            // Show desktop UI (icons and taskbar)
            // Note: This is called when installation completes without navigation
            // For Shop Now button, desktop UI will be shown after navigation by desktop.js
            this.showDesktopUI();
        }

        /**
         * Move to next step (legacy method, kept for compatibility)
         */
        nextStep() {
            this.handleNext();
        }

        /**
         * Move to previous step (legacy method, kept for compatibility)
         */
        previousStep() {
            this.handleBack();
        }
    }

    // Export to global scope
    window.InstallManager = InstallManager;
}

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
    module.exports = InstallManager;
}
